import { AppState } from 'src/AppState/AppState';
import { EventsCollectionQueryType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { computed, makeAutoObservable } from 'mobx';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { getSportStartTime } from 'src/components/CustomersBar/EventsTab/utils.js';
import { MarketTemplatesRecordType } from './utilsForMarkets';

export class SportCollection {
    public constructor(
        private readonly appState: AppState,
        private readonly sportId: string
    ) {
        makeAutoObservable(this);
    }

    @computed private get allEventsList(): EventsCollectionQueryModel | null {
        const query: EventsCollectionQueryType = {
            sport: [this.sportId],
            'startTime[from]': getSportStartTime(this.sportId),
            filterMarketStatus: {
                noteq: ['new', 'settled', 'resulted'],
            },
        };

        return this.appState.websocketV2.getEventQuery(query);
    }

    @computed public get listView(): EventsCollectionQueryModel | null {
        const list = this.allEventsList;

        if (list === null) {
            return null;
        }

        const currentCompetition = this.appState.routerBackoffice.getParametersString('customers', 'competition') ?? '';
        const currentRegion = this.appState.routerBackoffice.getParametersString('customers', 'region') ?? '';

        const outList = list
            .filterBySportAndEvent((_sport, event): boolean => {
                if (currentCompetition === '') {
                    return event.tags['telebet-top-events'] === 'yes';
                } else {
                    return true;
                }
            })
            .filterByCompetition((competitionId: number): boolean => {
                if (currentCompetition === '') {
                    return true;
                } else {
                    return competitionId.toString() === currentCompetition;
                }
            })
            .filterBySportAndEvent((_sport, event): boolean => {
                if (currentRegion === '') {
                    return true;
                } else {
                    return event.tags['region'] === currentRegion;
                }
            });

        return outList;
    }

    @computed public get competitions(): Array<{ id: string; name: string }> {
        const list = this.allEventsList?.competitionForViewByDisplayOrder ?? [];

        const out = [
            {
                name: 'Top Events',
                id: '',
            },
        ];

        for (const item of list) {
            out.push({
                name: item.name,
                id: item.id.toString(),
            });
        }

        return out;
    }
}

export class TwoParticipantsSportsTabState {
    public readonly sports: MobxMapAutoNew<string, SportCollection>;

    public constructor(
        private readonly appState: AppState,
        public readonly marketTemplates: MarketTemplatesRecordType
    ) {
        this.sports = new MobxMapAutoNew((sportId: string) => new SportCollection(appState, sportId));
    }

    public changeRegion = (selectedRegion: string): void => {
        this.appState.routerBackoffice.setParamInNamespace('customers', 'region', selectedRegion);
        this.appState.routerBackoffice.removeParamInNamespace('customers', 'search');
    };

    public changeCompetition = (selectedCompetition: string | number): void => {
        const currentCometition = this.appState.routerBackoffice.getParametersString('customers', 'competition');

        if (currentCometition === selectedCompetition.toString()) {
            return;
        }

        this.appState.routerBackoffice.setParamInNamespace('customers', 'competition', selectedCompetition);
        this.appState.routerBackoffice.removeParamInNamespace('customers', 'event');
        this.appState.routerBackoffice.removeParamInNamespace('customers', 'search');
    };

    public onEventClick = (selectedEventId: number): void => {
        const eventId = this.appState.routerBackoffice.getParametersNumber('customers', 'event');

        if (selectedEventId === eventId) {
            this.appState.routerBackoffice.removeParamInNamespace('customers', 'event');
        } else {
            this.appState.routerBackoffice.setParamInNamespace('customers', 'event', selectedEventId);

            this.appState.marketsListState.closeMarket();
        }
    };

    public get currentRegion(): string {
        const currentRegion = this.appState.routerBackoffice.getParametersString('customers', 'region') ?? '';
        return currentRegion;
    }

    public get currentCompetition(): string {
        const currentCompetition = this.appState.routerBackoffice.getParametersString('customers', 'competition') ?? '';
        return currentCompetition;
    }

    public get selectedEventId(): number | null {
        const selectedEventId = this.appState.routerBackoffice.getParametersNumber('customers', 'event');
        return selectedEventId;
    }

    public changeMarket = (selectedMarket: string): void => {
        const currentMarket = this.appState.routerBackoffice.getParametersString('customers', 'market');

        if (currentMarket === selectedMarket) {
            return;
        }

        this.appState.routerBackoffice.setParamInNamespace('customers', 'market', selectedMarket);
    };

    public get currentMarket(): string | null {
        return this.appState.routerBackoffice.getParametersString('customers', 'market');
    }

    public get currentSport(): string {
        const currentSport = this.appState.routerBackoffice.getParametersString('customers', 'sport') ?? '';
        return currentSport;
    }
}
